import {Component, DoCheck, Inject, OnDestroy, OnInit} from '@angular/core';
import {EMPTY, forkJoin, Observable, Subscription} from 'rxjs';
import {expand, reduce} from 'rxjs/operators';
import {AppConstants} from '../../shared/constants/app-constants';
import {HomeAction} from './home.action';
import {DataManagerService} from '../../data/services/DataManager/data-manager.service';
import {CurrencyPipe, DatePipe} from '@angular/common';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {BarSeries} from '../../ui/bar-chart/payarc-bar-chart.component';
import {DonutSegment} from '../../ui/donut/payarc-donut.component';
import {SegOption} from '../../ui/segmented/payarc-segmented.component';
import {PAYARC_DATE_FORMATS} from '../../shared/material-date-formats';
import {summarizeDisputeChart} from '../../shared/utils/dispute-chart-summary';
import {menuHasPage} from '../nav-drawer/nav-shared';
import {MatDialog} from '@angular/material/dialog';
import {DetailRow} from '../../ui/detail-card/payarc-detail-card.component';
import {RowDetailDialogComponent, RowDetailDialogData} from '../../ui/row-detail-dialog/row-detail-dialog.component';
import {PayarcStatusBadgeComponent} from '../../ui/status-badge/payarc-status-badge.component';
import {ShellBreakpointService} from '../../services/shell-breakpoint.service';
import {DateFormatPipe} from '../../shared/custom-pipes/date-format-pipe/dateformatpipe';
import { sameDay } from '../../ui/date-clear/payarc-date-clear.component';

/** Visa's published VAMP "excessive" dispute-ratio threshold; not merchant-specific data. */
const VAMP_RATIO_LIMIT = 0.9;

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false,
    providers: [{
      provide: MAT_DATE_FORMATS,
      useValue: { ...PAYARC_DATE_FORMATS, display: { ...PAYARC_DATE_FORMATS.display, dateInput: { year: 'numeric', month: 'short', day: '2-digit' } } }
    }]
})
export class HomeComponent extends HomeAction implements OnInit, DoCheck, OnDestroy {

  readonly vampRatioLimit = VAMP_RATIO_LIMIT;

  dateSet = AppConstants.homeDayDifference;
  dateUnit = AppConstants.homeDayUnitDifference;
  rangeOptions: SegOption[] = this.dateSet.map(d => ({label: d.text, value: d.text}));
  today = new Date();
  fromDate: Date;
  toDate: Date;
  criteria: string;
  daySetActive: string;
  daySetActiveOffset: number;
  userInput: boolean;
  currentResolution: number;

  // Key metrics (GET /reports)
  totalSales: number;
  totalRefunds: number;
  netSales: number;
  avg_revenue: number;
  total_transactions: number;
  refundPercentage: number;
  t_disputes: number;
  p_disputes: number;
  tv_disputes: number;
  pv_disputes: number;

  // Sales over time (GET /reports · v_net_gross)
  salesBarCategories: string[] = [];
  salesBarSeries: BarSeries[] = [
    {label: 'Gross sales', color: 'var(--pa-mint)', values: []},
    {label: 'Refunds', color: 'var(--pa-navy)', values: []},
    {label: 'Dispute', color: 'var(--pa-blue-light)', values: []},
  ];

  // Card-type mix (GET /reports · s_card_type)
  cardTypeSegments: DonutSegment[] = [];

  /** "Where volume went" donut — derived straight from the key-metrics fields above
      (net sales / refunds / disputes) rather than its own stored field, since there's
      no separate fetch to hang an assignment off like cardTypeSegments/disputeDonutSegments. */
  get volumeDonutSegments(): DonutSegment[] {
    return [
      {label: 'Net sales', value: this.netSales || 0, color: 'var(--pa-mint)'},
      {label: 'Refunded', value: this.totalRefunds || 0, color: 'var(--pa-coral)'},
      {label: 'Disputed', value: this.tv_disputes || 0, color: 'var(--pa-blue-light)'},
    ];
  }

  /** Brand-respected colors for the actual card networks (Visa/Mastercard/AMEX/Discover) —
      this chart is specifically about telling networks apart, so their own recognizable
      colors read better here than the dashboard's generic accent palette. The remaining
      keys aren't networks (they're card-usage categories: debit/ATM/EBT/unknown/etc.), so
      there's no brand color to match and they keep their original distinguishing colors. */
  pieChartColorsLables = {
    'R': {color: '#FF6000', label: 'Discover'},
    'V': {color: '#1A1F71', label: 'Visa'},
    'X': {color: '#006FCF', label: 'AMEX'},
    'M': {color: '#EB001B', label: 'Master Card'},
    'J': {color: '#92CCDC', label: 'JCB'},
    1: {color: '#EB001B', label: 'Master'},
    'U': {color: '#0eaeee', label: 'Unknown'},
    'I': {color: '#3168A9', label: 'Diners Club'},
    'E': {color: '#3166AC', label: 'EBT Card'},
    'D': {color: '#674EA7', label: 'Debit Card'},
    'A': {color: '#7F6006', label: 'ATM Card'},
    'G': {color: '#6AA84F', label: 'Private Label Gift Card'},
    // processor sales outside the network columns (Fiserv debit / fleet cards) - GET /reports adds
    // this row so the card types add up to 100%
    'O': {color: '#8A94A6', label: 'Other'},
  };

  /** Same brand->logo mapping payment-list.component.ts uses for the real transactions list. */
  cardImgObj = {
    'R': '../../../assets/images/disc.png',
    'V': '../../../assets/images/visa.png',
    'X': '../../../assets/images/amex.png',
    'M': '../../../assets/images/master.png',
    'J': '../../../assets/images/jcb.png',
  };
  defaultCardImg = '../../../assets/images/card-default.png';

  // Needs attention (derived from already-fetched data, no extra call)
  notRespondedDisputes = 0;

  // Dispute resolution + trend (GET /dispute-chart). Shown only when the user's backend menu has the
  // Disputes page (same permission gate as the sidebar) - otherwise the whole widget is hidden rather
  // than shown as "unavailable" for a merchant who simply has no access to disputes.
  showDisputes = false;
  disputeChartLoading = true;
  disputeChartError = false;
  disputeDonutSegments: DonutSegment[] = [];
  disputeTotalCount = 0;
  /** new chargebacks / follow-up records (from /dispute-chart totals); null = not available */
  disputeNewCount: number = null;
  disputeFollowUpCount: number = null;
  disputeTrendCategories: string[] = [];
  disputeTrendSeries: BarSeries[] = [{label: 'Disputes', color: 'var(--pa-coral)', values: []}];

  private readonly disputeColorMap: Record<string, string> = {
    'Currently resolved in Merchant\'s favor': 'var(--pa-mint)',
    'Currently resolved in Bank\'s favor': 'var(--pa-navy)',
    'Pending': 'var(--pa-blue-light)',
    'RDR': 'var(--pa-blue-med)',
    'Responded': 'var(--pa-purple)',
    'Not Responded': 'var(--pa-coral)',
  };

  // Deposits (GET /deposit/summary) — paused along with the card in home.component.html (its
  // date field renders "Invalid date" against real data) and the fetchDeposits() call in
  // setUrls() below, so there's no unused request/parsing while nothing renders this state.
  // Re-enable all three together once the real field/format is confirmed.
  // depositsLoading = true;
  // depositsError = false;
  // depositsPeriodTotal: number = null;
  // depositsRows: { date: string; reference: string; amount: number }[] = [];

  // Subscriptions + Est. MRR (GET /subscriptions)
  subscriptionsLoading = true;
  subscriptionsError = false;
  subActiveCount = 0;
  subTrialCount = 0;
  subCanceledCount = 0;
  subEstMrr = 0;

  // VAMP ratio (GET /vamp/summary — known to 503 in some envs; degrades gracefully)
  // Shown only when the user's backend menu has the VAMP Details page (same permission gate as the
  // sidebar); otherwise the whole card is hidden and Recent activity expands to the full row.
  showVamp = false;
  vampLoading = true;
  vampError = false;
  vampRatio: number = null;
  // PDT-35824: VAMP is a monthly measure — Home shows the previous calendar month, the same
  // default period as VAMP Details, independent of the Analytics range.
  vampPeriodLabel = '';

  // Top ACH reject reasons (GET /ach-rejects, client-aggregated); only when ACH Returns is in the
  // user's backend menu — the same gate as the sidebar and the topbar search.
  showAchRejects = false;
  achLoading = true;
  achError = false;
  achReasons: { label: string; amount: number; count: number }[] = [];

  // Recent activity (GET /events). Shown only when the user's menu has the Events & Logs page
  // (same permission gate as the sidebar) - otherwise hide the card instead of showing "unavailable".
  showRecentActivity = false;
  activityLoading = true;
  activityError = false;
  recentActivity: any[] = [];

  // Recent transactions (GET /charges)
  txnsLoading = true;
  txnsError = false;
  recentTransactions: any[] = [];
  /** Mobile: a Recent transactions row opens the transaction detail panel (see openTransactionDetail). */
  txnRowsInteractive = false;
  private breakpointSub?: Subscription;

  get userAccount(): any {
    return JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_ACCOUNT_ID));
  }

  constructor(dataManager: DataManagerService, private _datePipe: DatePipe, snackBar: MatSnackBar,
              @Inject('LOCALSTORAGE') private localStorage: any,
              private matDialog: MatDialog, private shellBreakpoint: ShellBreakpointService) {
    super();
    this.snackBar = snackBar;
    this.dataManager = dataManager;
    this.daySetActiveOffset = 6;
    this.daySetActive = '1w';
    this.criteria = 'day';
    this.userInput = true;
    this.fromDate = this.addDays(new Date(), this.daySetActiveOffset);
    this._getAllStatesUrl = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_REPORTS;
    this.toDate = new Date();
    this.currentResolution = window.screen.width;
    this.loadFlag = true;
    this.setUrls();
  }

  ngOnInit() {
    // Recent transactions rows are interactive (focusable, Enter/tap opens the panel) only on mobile
    this.breakpointSub = this.shellBreakpoint.isDesktop$.subscribe(isDesktop => this.txnRowsInteractive = !isDesktop);
    // Not date-range scoped — fetched once, independent of the KPI/chart widgets.
    this.fetchSubscriptions();
    this.showAchRejects = menuHasPage(this.dataManager.menuArray, 'ach-rejects');
    if (this.showAchRejects) {
      this.fetchAchRejects();
    }
    // Recent activity only when the user's menu has Events & Logs; otherwise hide it (don't 403).
    this.showRecentActivity = menuHasPage(this.dataManager.menuArray, 'events-and-logs');
    if (this.showRecentActivity) {
      this.fetchRecentActivity();
    }
    this.fetchRecentTransactions();
    // VAMP card only for users whose menu has VAMP Details; otherwise hide it (don't fetch / 403).
    this.showVamp = this.hasVampDetailsMenu();
    if (this.showVamp) {
      this.fetchVamp();
    }
  }

  /**
   * True when the user's menu has the VAMP Details page. Unlike menuHasPage (first route segment
   * only), VAMP Details has two valid routes - 'vamp-details' AND the 'payment/vamp-details' alias
   * (app.routes.ts), whose root is 'payment' - so both are matched by their full path here.
   */
  private hasVampDetailsMenu(): boolean {
    const menus = Array.isArray(this.dataManager.menuArray) ? this.dataManager.menuArray : [];
    return menus.some(m => [m?.routes, m?.routerLink]
      .map(r => String(r ?? '').replace(/^\/+/, '').replace(/\/+$/, ''))
      .some(path => path === 'vamp-details' || path === 'payment/vamp-details'));
  }

  ngDoCheck() {
    if (window.screen.width !== this.currentResolution) {
      this.currentResolution = window.screen.width;
    }
  }

  /** The widgets need a range, so "clear" goes back to the default 1w preset. */
  get datesChanged(): boolean {
    return this.daySetActive !== '1w' || !sameDay(this.toDate, new Date());
  }

  clearDates() {
    // 1w counts back from toDate, so reset it to today first.
    this.toDate = new Date();
    this.onRangeChange('1w');
  }

  onRangeChange(value: string) {
    const match = this.dateSet.find(d => d.text === value);
    if (match) {
      this.dateSetFun(match);
    }
  }

  setUrls() {
    this.loadFlag = true;
    const from = this._datePipe.transform(this.fromDate, 'yyyy-MM-dd');
    const to = this._datePipe.transform(this.toDate, 'yyyy-MM-dd');
    this.getAllStates(this._getAllStatesUrl + '?from=' + from + '&to=' + to + '&criteria=' + this.criteria);
    this.fetchDisputeChart(from, to);
    // fetchDeposits() is paused while the Deposits card is commented out in the template (its
    // date field renders "Invalid date" against real data — see home.component.html) — no point
    // fetching and parsing data nothing renders. Re-enable alongside the card.
    // VAMP is not range-scoped (previous calendar month) — fetched once in ngOnInit.
  }

  dateSetFun(arg) {
    this.loadFlag = true;
    this.daySetActive = arg.text;
    this.dateUnit = arg.dateUnit;
    this.daySetActiveOffset = arg.offset;
    if (arg.is_today_start) {
      this.toDate = new Date();
      this.fromDate = this.addDaysTD(arg.text);
      this.userInput = false;
    } else if (arg.text === 'All') {
      if (this.userAccount[0].created_at) {
        this.toDate = new Date();
        this.fromDate = new Date('2019-01-01');
        const diffDays = this.dateDifference(this.fromDate, this.toDate);
        diffDays <= 28 ? this.criteria = 'day' : diffDays <= 150 ? this.criteria = 'week' :
          this.criteria = 'month';
      } else {
        this.showSnackBar('User account info issue', 'Ok', 3000);
      }
    } else if (arg.text === '1w') {
      this.fromDate = new Date(this.toDate.getFullYear(), this.toDate.getMonth(), this.toDate.getDate() - 6);
    } else if (arg.text === '4w') {
      this.fromDate = new Date(this.toDate.getFullYear(), this.toDate.getMonth(), this.toDate.getDate() - 27);
    } else if (arg.text === '1y') {
      this.fromDate = new Date(this.toDate.getFullYear() - 0.5, this.toDate.getMonth(), this.toDate.getDate() - 1);
    }
    if (arg.dateUnit.length > 0) {
      for (const criteria of arg.dateUnit) {
        if (criteria.is_default) {
          this.criteria = criteria.criteria;
        }
      }
    }
    this.setUrls();
  }

  setDateFrom() {
    if (this.toDate < this.fromDate) {
      this.toDate = new Date(this.fromDate);
    }
    const diffDays = this.dateDifference(this.fromDate, this.toDate);
    diffDays <= 28 ? this.criteria = 'day' : diffDays <= 150 ? this.criteria = 'week' :
      this.criteria = 'month';
    this.dateUnit = [];
    this.daySetActive = '1';
    this.setUrls();
  }

  /**
   * func to set date based on option selected
   * @param date
   * @param days
   * @returns {Date}
   */
  addDays(date: Date, days: number): Date {
    const d = date;
    d.setDate(date.getDate() - days);
    return d;
  }

  addDaysTD(key) {
    const date = new Date();
    switch (key) {
      case 'Mtd':
        date.setDate(1);
        break;
      case 'Qtd':
        if (date.getMonth() <= 2) {
          date.setMonth(1);
        } else if (date.getMonth() > 2 && date.getMonth() <= 5) {
          date.setMonth(3);
        } else if (date.getMonth() > 5 && date.getMonth() <= 8) {
          date.setMonth(6);
        } else if (date.getMonth() > 8 && date.getMonth() <= 11) {
          date.setMonth(9);
        }
        date.setDate(1);
        break;
      case 'Ytd':
        date.setDate(1);
        date.setMonth(0);
        break;
    }
    return date;
  }

  protected passAllStatesResponse(res) {
    this.loadFlag = false;
    this.netSales = res.n_sales;
    this.avg_revenue = res.avg_tranx;
    this.totalRefunds = res.t_refunds;
    this.total_transactions = res.t_tranx;
    this.refundPercentage = res.p_refunds;
    this.totalSales = res.t_sales;
    this.t_disputes = res.t_disputes;
    this.p_disputes = res.p_disputes;
    this.tv_disputes = res.tv_disputes;
    this.pv_disputes = res.pv_disputes;
    this.setSalesBarSeries(res.v_net_gross ? res.v_net_gross : []);
    this.setCardTypeSegments(res.s_card_type ? res.s_card_type : []);
  }

  protected passError(error) {
    this.loadFlag = false;
  }

  private setSalesBarSeries(rows: any[]) {
    const categories: string[] = [];
    const gross: number[] = [];
    const refunds: number[] = [];
    const disputes: number[] = [];
    rows.forEach(row => {
      categories.push(this.formatBarLabel(row.label));
      gross.push((row.gross_sales || 0) / 100);
      refunds.push((row.refund_amount || 0) / 100);
      disputes.push((row.dispute_amount || 0) / 100);
    });
    this.salesBarCategories = categories;
    this.salesBarSeries = [
      {label: 'Gross sales', color: 'var(--pa-mint)', values: gross},
      {label: 'Refunds', color: 'var(--pa-navy)', values: refunds},
      {label: 'Dispute', color: 'var(--pa-blue-light)', values: disputes},
    ];
  }

  private formatBarLabel(label): string {
    if (typeof label !== 'number') {
      return label;
    }
    const last = label.toString().split('').pop();
    if (label > 3 && label < 21) {
      return label + 'th Week';
    }
    if (last === '1') {
      return label + 'st Week';
    }
    if (last === '2') {
      return label + 'nd Week';
    }
    if (last === '3') {
      return label + 'rd Week';
    }
    return label + 'th Week';
  }

  private setCardTypeSegments(rows: any[]) {
    this.cardTypeSegments = rows
      .filter(row => !this.isNull(row.card_type) && this.pieChartColorsLables[row.card_type])
      .map(row => ({
        label: this.pieChartColorsLables[row.card_type].label,
        value: Number(row.card_tranx_percentage),
        color: this.pieChartColorsLables[row.card_type].color,
      }));
  }

  private fetchDisputeChart(from: string, to: string) {
    // Hide the dispute widgets entirely when the user has no Disputes page in their menu (no
    // permission) - don't fire the request (it would 403) or render an "unavailable" card.
    this.showDisputes = menuHasPage(this.dataManager.menuArray, 'disputes');
    if (!this.showDisputes) {
      this.disputeChartLoading = false;
      return;
    }
    this.disputeChartLoading = true;
    this.disputeChartError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_DISPUTE_CHART
      + '?report_date[gte]=' + from + '&report_date[lte]=' + to;
    this.dataManager.getRequest(url, 0).subscribe(
      (res) => this.passDisputeChartResponse(res),
      () => {
        this.disputeChartLoading = false;
        this.disputeChartError = true;
        // Clear everything derived from /dispute-chart so a failed refresh can't leave the
        // previous range's numbers next to the /reports fallback in the KPI card.
        this.disputeNewCount = null;
        this.disputeFollowUpCount = null;
        this.disputeTotalCount = 0;
        this.disputeDonutSegments = [];
        this.disputeTrendCategories = [];
        this.disputeTrendSeries = [{label: 'Disputes', color: 'var(--pa-coral)', values: []}];
        this.notRespondedDisputes = 0;
      }
    );
  }

  /**
   * "New Disputes" KPI: new chargebacks (primary cases, /dispute-chart totals.primary) - the same
   * set of cases as the "New dispute %" next to it and the Disputes Volume card, which come from
   * /reports and count primary cases only (PDT-35957: the card used to show every case, primaries
   * plus follow-ups, next to the volume of the primaries alone). Follow-ups and all cases are shown
   * on the card's second line. Falls back to /reports' t_disputes when /dispute-chart has no totals.
   */
  get kpiDisputesTotal(): number {
    return this.disputeNewCount !== null ? this.disputeNewCount : (this.t_disputes || 0);
  }

  /** New chargebacks as % of transactions (same formula as /reports' p_disputes, same counts as the cards). */
  get kpiDisputePercent(): number {
    if (this.disputeNewCount === null) {
      return this.p_disputes || 0;
    }
    const tranx = Number(this.total_transactions || 0);
    return tranx > 0 ? Number(((this.disputeNewCount / tranx) * 100).toFixed(2)) : 0;
  }

  private passDisputeChartResponse(res: any) {
    this.disputeChartLoading = false;
    // Same reading as the Disputes page (outcome partition only; summing every bucket of
    // pi_chart_data counted each dispute twice).
    const summary = summarizeDisputeChart(res);
    this.disputeTotalCount = summary.total;
    this.disputeNewCount = summary.primary;
    this.disputeFollowUpCount = summary.secondary;
    this.disputeDonutSegments = summary.outcome
      .filter(r => Number(r.dispute_count) > 0)
      .map(r => ({
        label: r.data_type,
        value: Number(r.dispute_count),
        color: this.disputeColorMap[r.data_type] || 'var(--pa-blue)',
      }));

    this.notRespondedDisputes = summary.notResponded;

    const trend: any[] = res?.dispute_count_chart_data ?? [];
    this.disputeTrendCategories = trend.map(r => r.label);
    this.disputeTrendSeries = [{
      label: 'Disputes',
      color: 'var(--pa-coral)',
      values: trend.map(r => Number(r.dispute_count || 0)),
    }];
  }

  // Paused along with the Deposits card and its state above — see that comment.
  // private fetchDeposits(from: string, to: string) {
  //   this.depositsLoading = true;
  //   this.depositsError = false;
  //   const url = AppConstants.BASE_URL + '/deposit/summary?from_date=' + from + '&to_date=' + to;
  //   this.dataManager.getRequest(url, 0).subscribe(
  //     (res: any) => {
  //       this.depositsLoading = false;
  //       const rowsData = res?.data?.rows_data ?? {};
  //       this.depositsPeriodTotal = rowsData?.grand_total?.total_amount ?? 0;
  //       this.depositsRows = this.extractDepositRows(rowsData);
  //     },
  //     () => {
  //       this.depositsLoading = false;
  //       this.depositsError = true;
  //     }
  //   );
  // }
  //
  // /** `rows_data` is keyed by settlement group, each holding its own `row_data` array (see
  //  * deposits-report.component.ts, which reads the same /deposit/summary shape) — flattens every
  //  * group's DEPOSIT-type entries (the actual payout batches, as opposed to fees/adjustments/etc.)
  //  * down to the handful most recent ones for this glance card, matching the prototype's
  //  * date/reference/amount mini table (design/prototype/dashboard.html's "When you'll get paid"). */
  // private extractDepositRows(rowsData: any): { date: string; reference: string; amount: number }[] {
  //   const rows = Object.keys(rowsData)
  //     .filter(key => key !== 'grand_total')
  //     .flatMap(key => rowsData[key]?.row_data ?? [])
  //     .filter((r: any) => r.transType === 'DEPOSIT' && r.amount)
  //     .map((r: any) => ({
  //       date: r.effective_date,
  //       reference: r.batch_reference_number || r.cb_reference_number || '--',
  //       amount: r.display_amount ?? r.amount,
  //     }));
  //   rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  //   return rows.slice(0, 5);
  // }

  /** Formatted like VAMP Details (DecimalPipe '1.2-4'), e.g. 0.0588. */
  get vampRatioText(): string {
    return this.vampRatio === null || this.vampRatio === undefined
      ? ''
      : this.vampRatio.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  private fetchVamp() {
    this.vampLoading = true;
    this.vampError = false;
    // Previous calendar month — the same default period as VAMP Details (vamp.component.ts).
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const from = this._datePipe.transform(monthStart, 'yyyy-MM-dd');
    const to = this._datePipe.transform(monthEnd, 'yyyy-MM-dd');
    this.vampPeriodLabel = this._datePipe.transform(monthStart, 'MMM y');
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_VAMP + '/summary?from=' + from + '&to=' + to;
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.vampLoading = false;
        const row = res?.data?.[0];
        // A missing / empty ratio is "no data" (empty state), not 0% — Number(null) and
        // Number('') would both coerce to 0.
        const raw = row?.vamp_ratio_pct;
        const ratio = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
        this.vampRatio = isFinite(ratio) ? ratio : null;
      },
      () => {
        this.vampLoading = false;
        this.vampError = true;
      }
    );
  }

  private fetchSubscriptions() {
    this.subscriptionsLoading = true;
    this.subscriptionsError = false;
    const base = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_SUBSCRIPTIONS;
    const countUrl = (status: string) => base + '?limit=1&page=1&status[in]=' + status;

    forkJoin({
      active: this.dataManager.getRequest(countUrl('active'), 0),
      trial: this.dataManager.getRequest(countUrl('trial'), 0),
      canceled: this.dataManager.getRequest(countUrl('canceled'), 0),
      activeMrr: this.fetchActiveSubscriptionAmounts(),
    }).subscribe(
      (res: any) => {
        this.subscriptionsLoading = false;
        this.subActiveCount = res.active?.meta?.pagination?.total ?? 0;
        this.subTrialCount = res.trial?.meta?.pagination?.total ?? 0;
        this.subCanceledCount = res.canceled?.meta?.pagination?.total ?? 0;
        this.subEstMrr = res.activeMrr;
      },
      () => {
        this.subscriptionsLoading = false;
        this.subscriptionsError = true;
      }
    );
  }

  /**
   * Sums plan.data.amount across every page of active subscriptions (not just the first),
   * following the same links.next-truthiness convention BaseListComponent uses elsewhere —
   * an account with more active subscriptions than one page must not understate Est. MRR.
   */
  private fetchActiveSubscriptionAmounts(limit = 200): Observable<number> {
    const base = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_SUBSCRIPTIONS;
    const pageUrl = (page: number) => base + '?limit=' + limit + '&page=' + page + '&status[in]=active&include=plan';
    return this.dataManager.getRequest(pageUrl(1), 0).pipe(
      expand((res: any, index: number) =>
        res?.meta?.pagination?.links?.next ? this.dataManager.getRequest(pageUrl(index + 2), 0) : EMPTY
      ),
      reduce((sum: number, res: any) =>
        sum + (res?.data ?? []).reduce((s: number, row: any) => s + (row?.plan?.data?.amount || 0), 0), 0)
    );
  }

  private fetchAchRejects() {
    this.achLoading = true;
    this.achError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_ACH_REJECTS + '?limit=100&page=1';
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.achLoading = false;
        const rows: any[] = res?.data ?? [];
        const byReason = new Map<string, { amount: number; count: number }>();
        rows.forEach(row => {
          const label = row.reason_description || row.reason_code || 'Unknown';
          const entry = byReason.get(label) || {amount: 0, count: 0};
          entry.amount += Number(row.amount || 0);
          entry.count += 1;
          byReason.set(label, entry);
        });
        this.achReasons = Array.from(byReason.entries())
          .map(([label, v]) => ({label, amount: v.amount, count: v.count}))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 5);
      },
      () => {
        this.achLoading = false;
        this.achError = true;
      }
    );
  }

  private fetchRecentActivity() {
    this.activityLoading = true;
    this.activityError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_EVENT + '?limit=8&page=1';
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.activityLoading = false;
        this.recentActivity = res?.data ?? [];
      },
      () => {
        this.activityLoading = false;
        this.activityError = true;
      }
    );
  }

  ngOnDestroy(): void {
    this.breakpointSub?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Mobile only (below the app's 769px shell breakpoint): a Recent transactions row opens the
   * charge in the shared row detail panel, with a link to the full payment details. On a phone the
   * table only shows Date / Card; desktop keeps the full row and no panel (as Batch Detail).
   */
  openTransactionDetail(t: any): void {
    if (this.shellBreakpoint.isDesktopNow) {
      return;
    }
    const card = t.card?.data;
    const amount = new CurrencyPipe('en-US').transform((t.amount || 0) / 100, 'USD', 'symbol', '1.2-2');
    const rows: DetailRow[] = [
      { label: 'Amount', value: amount || '--' },
      // same formatting as the table's dateFormat pipe (created_at is a unix timestamp)
      { label: 'Date', value: t.created_at ? new DateFormatPipe().transform(t.created_at) : '--' },
      { label: 'Customer', value: card?.customer_id || '--' },
      { label: 'Transaction ID', value: t.id || '--' },
    ];
    const variant = Object.assign(new PayarcStatusBadgeComponent(), { status: t.status }).resolvedVariant;
    const data: RowDetailDialogData = {
      title: 'Transaction detail',
      rows,
      status: t.status ? { label: t.status, tone: variant } : undefined,
      card: { number: '•••• ' + (card?.last4digit || '----'), img: this.cardImgObj[card?.brand] || this.defaultCardImg },
      action: t.id ? { label: 'View transaction', link: '/payment/payment-details', queryParams: { id: t.id } } : undefined,
    };
    this.matDialog.open(RowDetailDialogComponent, {
      data,
      panelClass: 'pa-shell-dialog',
      width: '560px',
      maxWidth: '94vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  private fetchRecentTransactions() {
    this.txnsLoading = true;
    this.txnsError = false;
    const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_CHARGE + '?limit=8&page=1';
    this.dataManager.getRequest(url, 0).subscribe(
      (res: any) => {
        this.txnsLoading = false;
        this.recentTransactions = res?.data ?? [];
      },
      () => {
        this.txnsLoading = false;
        this.txnsError = true;
      }
    );
  }
}
